import Jazzicon, { jsNumberForAddress } from 'react-jazzicon';
import {
  Avatar,
  Flex,
  HStack,
  Link,
  Tag,
  Text,
  Tooltip,
  VStack
} from '@chakra-ui/react';
import { FC } from 'react';
import { useModal } from 'store/modals';
import { Proposal, Space, Strategy } from 'types/snapshot';
import { truncate } from 'utils/address';
import { RiExternalLinkLine } from 'react-icons/ri';
import { getFullDate, typeToVotingSystem } from 'helpers/snapshot';
import { ModalType } from 'components/modals/types';
import { useWeb3React } from 'hooks/web3';

type Props = {
  proposal: Proposal;
  space: Space;
};

const ProposalInformation: FC<Props> = ({ proposal, space }) => {
  const { openModal } = useModal();
  const { account } = useWeb3React();

  return (
    <VStack w='100%'>
      <Flex w='100%' justifyContent='space-between'>
        <Text>Project</Text>
        <Text textAlign='right'>{proposal.space.name}</Text>
      </Flex>
      <Flex w='100%' justifyContent='space-between'>
        <Text>Strategies</Text>
        <HStack>
          {proposal.strategies.map((strategy: Strategy) => (
            <Tooltip
              key={strategy.name}
              hasArrow
              label={<Text>{strategy.params.symbol}</Text>}
              fontSize='md'
              placement='top'
            >
              <Avatar
                ignoreFallback
                bg='transparent'
                src={`https://ipfs.io/ipfs/${space.avatar.replace(
                  'ipfs://',
                  ''
                )}`}
                size='xs'
                name='Strategy'
                borderWidth='1px'
                _hover={{ cursor: 'pointer' }}
                onClick={() =>
                  openModal(ModalType.strategies, {
                    strategies: proposal?.strategies
                  })
                }
              />
            </Tooltip>
          ))}
        </HStack>
      </Flex>

      <Flex w='100%' justifyContent='space-between'>
        <Text>Author</Text>
        <HStack>
          {proposal.author && (
            <Jazzicon
              diameter={20}
              seed={jsNumberForAddress(proposal.author)}
            />
          )}
          <Text>
            {account && account === proposal.author
              ? 'You'
              : proposal.authorEns
              ? proposal.authorEns
              : truncate(proposal.author)}
          </Text>
          <Link
            href={`https://etherscan.io/address/${proposal.author}`}
            isExternal
            fontSize='lg'
          >
            <RiExternalLinkLine />
          </Link>
          {space.members.find((admin: string) => proposal.author === admin) && (
            <Tag variant='outline'>core</Tag>
          )}
        </HStack>
      </Flex>

      <Flex w='100%' justifyContent='space-between'>
        <Text>IPFS</Text>
        <HStack>
          <Text>#{proposal.ipfs.substring(0, 7)}</Text>
          <Link
            href={`https://cloudflare-ipfs.com/ipfs/${proposal.ipfs}`}
            isExternal
            fontSize='lg'
          >
            <RiExternalLinkLine />
          </Link>
        </HStack>
      </Flex>

      <Flex w='100%' justifyContent='space-between'>
        <Text>Voting system</Text>
        <Text textAlign='right'>{typeToVotingSystem(proposal.type)}</Text>
      </Flex>
      <Flex w='100%' justifyContent='space-between'>
        <Text>Start date</Text>
        <Text textAlign='right'>{getFullDate(proposal.start)}</Text>
      </Flex>
      <Flex w='100%' justifyContent='space-between'>
        <Text>End date</Text>
        <Text textAlign='right'>{getFullDate(proposal.end)}</Text>
      </Flex>

      <Flex w='100%' justifyContent='space-between'>
        <Text>Snapshot</Text>
        <HStack>
          <Text>{parseInt(proposal.snapshot).toLocaleString()}</Text>
          <Link
            href={`https://etherscan.io/block/${proposal.snapshot}`}
            isExternal
            fontSize='lg'
          >
            <RiExternalLinkLine />
          </Link>
        </HStack>
      </Flex>
    </VStack>
  );
};

export default ProposalInformation;
